import { getImageWithFocalPoints } from '@disco/api/imgixClient'
import { blobTobase64 } from '@disco/utils/convertImage'
import { pushUINotification } from '@disco/utils/notifications'
import { getAlbumImage } from '@disco/utils/playlists'
import clsx from 'clsx'

import React, { useState } from 'react'
import { useDispatch } from 'hooks/use-redux'
import type { Shortlist } from 'types/shortlist'
import { updateShortlist } from '@disco/state-management/actions/ShortlistActions'
import ImageRecommendations from 'components/modals/image-recommendations-modal'
import CroppingTool from 'components/ui/cropping-tool'
import FileSelector from 'components/ui/file-selector'
import useLocalStorage from 'hooks/use-local-storage'
import styles from './index.module.css'

const buildURL = ({ url, focal_points, ar }) => {
  if (!url) return null
  return getImageWithFocalPoints({
    image: url,
    params: {
      focal_points,
      ar,
    },
  })
}

const getOriginalArtistImage = artist => {
  if (!artist?.image_url) return null

  return artist?.focal_points
    ? getImageWithFocalPoints({
        image: artist.image_url,
        params: {
          focal_points: artist.focal_points,
        },
      })
    : artist.image_url
}

const getArtistImage = shortlist => {
  const showcase = shortlist.showcase
  let result = null
  if (!showcase) return result
  result =
    showcase?.artist_override_image_preview ||
    buildURL({
      url: showcase.artist_override_image_url,
      focal_points: showcase.focal_points,
      ar: '14:9',
    }) ||
    getOriginalArtistImage(showcase?.artist)

  if (shortlist.showcase?.hide_artist_image) {
    result = null
  }

  return result
}

const getDefaultAlbumImage = (shortlist: Shortlist) => {
  if (shortlist.color) return null
  return getAlbumImage(shortlist)
}

const getShortlistAlbumImage = (shortlist: Shortlist) => {
  const defaultAlbumImage = getDefaultAlbumImage(shortlist)
  return (
    shortlist.album_image_preview ||
    buildURL({
      url: shortlist.album_image_url,
      focal_points: shortlist.focal_points,
      ar: '1:1',
    }) ||
    defaultAlbumImage
  )
}

type Props = {
  onChangeShowcase: (...args: Array<any>) => any
  shortlist: Shortlist
  type: 'artist' | 'album'
  disabled?: boolean
}

const ImageSelector = ({
  onChangeShowcase,
  shortlist,
  type,
  disabled,
}: Props): React.ReactElement<'div'> => {
  const dispatch = useDispatch()
  const [croppingToolOptions, setCroppingToolOptions] = useState<{
    open?: boolean
    image?: string | null | undefined
    overlayStyle?: Record<string, any> | null | undefined
    onClose?: () => void
    onApply?: (opts: {
      base64Image: string
      imgixOptions: Record<string, any>
      focal_points: Record<string, any>
    }) => void
  }>({
    open: false,
  })
  const [showImageRecommendations, setShowImageRecommendations] =
    useState(false)
  const [dismissedImageRecommendations, setDismissedImageRecommendations] =
    useLocalStorage<Date | null | undefined>(`image_recommendations`, null)
  const config = {
    artist: {
      height: 345,
      getImage: getArtistImage,
      previewStyle: {
        backgroundPosition: '50% 50%',
      },
      caption: 'Artist image (landscape, minimum 2000 px wide)',
      overlayStyle: {
        width: '550px',
        height: '353px',
      },
      onApply: ({ base64Image, result, focal_points }) => {
        onChangeShowcase({
          artist_override_image_preview: base64Image,
          artist_override_image: result,
          artist_override_image_url: null,
          hide_artist_image: false,
          focal_points,
        })
      },
      onClear: () => {
        const currentImage = getArtistImage(shortlist)
        const hide_artist_image =
          currentImage === getOriginalArtistImage(shortlist.showcase?.artist)
        onChangeShowcase({
          artist_override_image_preview: null,
          artist_override_image: null,
          artist_override_image_url: null,
          focal_points: null,
          hide_artist_image,
        })
      },
    },
    album: {
      height: 98,
      getImage: getShortlistAlbumImage,
      previewStyle: {
        backgroundPosition: '50% 50%',
      },
      caption: 'Album image',
      overlayStyle: {
        width: '250px',
        height: '250px',
      },
      onApply: ({ base64Image, result, focal_points }) => {
        dispatch(
          updateShortlist(shortlist.id, {
            album_image_preview: base64Image,
            album_image: result,
            focal_points,
            color: null,
          }),
        )
      },
      onClear: () => {
        const defaultAlbumImage = getDefaultAlbumImage(shortlist)
        const albumImage = getShortlistAlbumImage(shortlist)
        let color = shortlist.color

        if (defaultAlbumImage === albumImage) {
          color = '#EBEDF1'
        }

        dispatch(
          updateShortlist(shortlist.id, {
            album_image_preview: null,
            album_image: null,
            album_image_url: null,
            focal_points: null,
            color,
          }),
        )
      },
    },
  }
  const currentConfig = config[type]

  const openCroppingTool = ({ result, onApply, overlayStyle }) => {
    setCroppingToolOptions({
      image: String(result),
      onApply: ({ base64Image, imgixOptions }) => {
        onApply({
          base64Image,
          imgixOptions,
        })
        setCroppingToolOptions({
          image: null,
        })
      },
      onClose: () => {
        setCroppingToolOptions({
          image: null,
        })
      },
      overlayStyle,
    })
  }

  const checkImageRecommendations = () => {
    if (!dismissedImageRecommendations && !currentConfig.getImage(shortlist)) {
      setShowImageRecommendations(true)
      setDismissedImageRecommendations(new Date())
      return false
    }
  }

  const onDrop = files => {
    const cancelDrop = checkImageRecommendations() === false
    if (cancelDrop) return
    const file = files && files[0]
    const MAX_SIZE = 2
    const fileSizeInMB = file?.size / (1024 * 1024)

    if (file && fileSizeInMB >= MAX_SIZE) {
      return pushUINotification({
        message: `The maximum image size is ${MAX_SIZE}MB.`,
        level: 'error',
      })
    }

    if (file) {
      blobTobase64(file).then(result => {
        if (type === 'artist') {
          openCroppingTool({
            result,
            onApply: ({ base64Image, imgixOptions }) => {
              const focal_points = {
                focal_point_x: imgixOptions['fp-x'],
                focal_point_y: imgixOptions['fp-y'],
                focal_point_z: imgixOptions['fp-z'],
              }
              currentConfig.onApply({
                result,
                base64Image,
                focal_points,
              })
            },
            overlayStyle: currentConfig.overlayStyle,
          })
        } else {
          currentConfig.onApply({
            result,
            base64Image: result,
            focal_points: null,
          })
        }
      })
    } else {
      currentConfig.onClear()
    }
  }

  return (
    <div
      className={clsx({
        [styles.imageSelectorDisabled]: disabled,
      })}
    >
      <FileSelector
        onClick={checkImageRecommendations}
        type="image"
        height={currentConfig.height}
        image={currentConfig.getImage(shortlist)}
        caption={[currentConfig.caption]}
        onDrop={onDrop}
        showRemove={Boolean(currentConfig.getImage(shortlist))}
        dnd
      />
      {croppingToolOptions.image && (
        <CroppingTool
          src={croppingToolOptions.image}
          overlayStyle={croppingToolOptions.overlayStyle}
          onClose={croppingToolOptions.onClose}
          onApply={croppingToolOptions.onApply}
          withImageRecommendations={type === 'artist'}
        />
      )}
      {showImageRecommendations && (
        <ImageRecommendations
          onClose={() => setShowImageRecommendations(false)}
        />
      )}
    </div>
  )
}

export default ImageSelector
